import React, { useState, useEffect } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { SHOP, reviewBase } from "../shop";

// ─── Colour tokens ────────────────────────────────────────────────────────────

const BRAND = SHOP.accent;
const ORDER_REVIEW_BASE = `${reviewBase()}/order-review`;
const SAMPLE_LINK = `${ORDER_REVIEW_BASE}/preview-sample-id`;

// ─── Default order email texts (mirrors ORDER_EMAIL_DEFAULTS in functions) ───

export const ORDER_EMAIL_TEXT_DEFAULTS = {
  fromLocal: "gestaltung",
  orderReminder1Days: 2,
  orderReminder2Days: 5,
  orderReminder3Days: 8,

  welcomeSubject:
    "Deine Bestellung bei " + SHOP.name + " – bitte kurz bestätigen {{ORDER}}",
  welcomeBody:
    "Hallo {{NAME}},\n\n" +
    "vielen Dank für deine Bestellung! Bevor wir mit der Gestaltung beginnen,\n" +
    "bitte einmal kurz bestätigen, dass alle Details stimmen.\n\n" +
    "{{ITEMS}}\n\n" +
    "{{LINK}}\n\n" +
    "Bei Fragen antworte einfach auf diese E-Mail.\n\n" +
    "Liebe Grüße\n" +
    "Dein Team von " + SHOP.name,

  orderReminder1Subject:
    "Erinnerung: Bitte Bestellung bestätigen {{ORDER}}",
  orderReminder1Body:
    "Hallo {{NAME}},\n\n" +
    "deine Bestellung wartet noch auf Bestätigung.\n\n" +
    "{{LINK}}\n\n" +
    "Liebe Grüße\n" +
    "Dein " + SHOP.name + " Team",

  orderReminder2Subject:
    "2. Erinnerung: Bestelldetails noch offen {{ORDER}}",
  orderReminder2Body:
    "Hallo {{NAME}},\n\n" +
    "wir haben noch keine Rückmeldung erhalten.\n\n" +
    "{{LINK}}\n\n" +
    "Liebe Grüße\n" +
    "Dein " + SHOP.name + " Team",

  orderReminder3Subject:
    "Letzte Erinnerung: Bitte Bestellung bestätigen {{ORDER}}",
  orderReminder3Body:
    "Hallo {{NAME}},\n\n" +
    "bitte bestätige deine Bestellung hier:\n\n" +
    "{{LINK}}\n\n" +
    "Ohne Bestätigung kann es zu Verzögerungen kommen.\n\n" +
    "Liebe Grüße\n" +
    "Dein " + SHOP.name + " Team",

  orderConfirmedSubject:
    "Bestätigt – wir starten mit deiner Bestellung! {{ORDER}}",
  orderConfirmedBody:
    "Hallo {{NAME}},\n\n" +
    "vielen Dank für deine Bestätigung! 🎉\n" +
    "Wir starten jetzt mit der Produktion.\n\n" +
    "Liebe Grüße\n" +
    "Dein " + SHOP.name + " Team",

  orderModificationSubject:
    "Wir haben deinen Änderungswunsch erhalten {{ORDER}}",
  orderModificationBody:
    "Hallo {{NAME}},\n\n" +
    "vielen Dank für dein Feedback!\n\n" +
    "{{FEEDBACK}}\n\n" +
    "Wir kümmern uns schnellstmöglich darum.\n\n" +
    "Liebe Grüße\n" +
    "Dein " + SHOP.name + " Team",

  orderConfirmedReceiptSubject:
    "Bestellbestätigung eingegangen – ETSY #{{ORDER}} 🤎",
  orderConfirmedReceiptOpening:
    "Hallo {{NAME}},\n\n" +
    "vielen Dank für deine Bestätigung. " +
    "Wir haben deine Angaben erhalten und starten " +
    "schnellstmöglich mit der Produktion.",
  orderModificationReceiptSubject:
    "Bestellbestätigung eingegangen – ETSY #{{ORDER}} 🤎",
  orderModificationReceiptOpening:
    "Hallo {{NAME}},\n\n" +
    "vielen Dank für deine Rückmeldung. " +
    "Anbei die Zusammenfassung deiner Angaben.",

  emailFooter: "© " + SHOP.name + " · " + SHOP.email,

  orderEtsy2Reminder:
    "Hallo {{NAME}},\n" +
    "wir haben dir bereits zwei E-Mails zur Bestätigung deiner Bestellung {{ORDER}} geschickt.\n" +
    "Falls du sie nicht erhalten hast, prüfe bitte auch deinen Spam-Ordner.\n" +
    "Bei Fragen kannst du uns hier direkt antworten.\n" +
    "Liebe Grüße, dein " + SHOP.name + " Team",

  orderEtsy3Reminder:
    "Hallo {{NAME}},\n" +
    "letzte Erinnerung: deine Bestellung {{ORDER}} wartet noch auf deine Bestätigung.\n" +
    "Ohne Rückmeldung müssen wir die Bestellung möglicherweise stornieren.\n" +
    "Bitte melde dich kurzfristig – entweder hier oder unter " + SHOP.email + ".\n" +
    "Liebe Grüße, dein " + SHOP.name + " Team",

  minDaysBeforeEvent: 5,
  eventDateWarningText:
    "Bitte prüfen: das Datum der Personalisierung, " +
    "liegt vor der Übergabe an DHL",
};

// ─── HTML rendering helpers ───────────────────────────────────────────────────

const linkButton = (href) =>
  `<p style="text-align:center;margin:20px 0;">` +
  `<a href="${href}" style="background:${BRAND};color:#ffffff;` +
  `padding:12px 28px;border-radius:6px;text-decoration:none;` +
  `font-weight:bold;font-size:15px;display:inline-block;">` +
  `Bestelldetails pr\u00fcfen &#8594;</a></p>`;

// Mirrors renderItemsBlock in functions/index.js so the preview reflects
// what customers actually see. Handles both new Multi-Personalization
// (items[].personalizations = array of {label,value,type}) and the legacy
// single string field (items[].personalization) via fallback.
const itemsBlock = (items) => {
  if (!items || items.length === 0) return "";
  return items.map((item, i) => {
    // Preview-friendly shortcut: a bare string still renders as before.
    if (typeof item === "string") {
      return `<div style="background:#fdf7f4;border-radius:6px;` +
        `padding:12px 16px;margin:8px 0;">` +
        `<p style="margin:0;color:#4a5568;font-size:14px;">` +
        `<strong>${i + 1})</strong> ${escHtml(item)}</p>` +
        `</div>`;
    }
    const title = escHtml(item.title || "");
    const variant = escHtml(item.variant || "");
    const personFields = Array.isArray(item.personalizations) &&
                         item.personalizations.length > 0
      ? item.personalizations
      : (item.personalization
          ? [{ label: "Personalisierung",
               value: item.personalization, type: "text" }]
          : []);
    const personRows = personFields.map((p) => {
      const label = escHtml(p.label || "Personalisierung");
      let valueHtml;
      if (p.type === "file") {
        valueHtml = `<a href="${escHtml(p.value)}" ` +
          `style="color:#B8A09A;text-decoration:underline;" ` +
          `target="_blank" rel="noopener">📎 Datei ansehen</a>`;
      } else {
        valueHtml = escHtml(p.value || "").replace(/\n/g, "<br>");
      }
      return `<div style="margin:6px 0;">` +
        `<strong style="color:#5a4a44;font-size:12px;">${label}:</strong>` +
        `<div style="color:#2d3748;margin-top:2px;">${valueHtml}</div>` +
        `</div>`;
    }).join("");
    const personBlock = personRows
      ? `<div style="background:#fdf7f4;border-left:3px solid #B8A09A;` +
        `padding:10px 14px;margin:8px 0;color:#2d3748;font-size:13px;">` +
        personRows + `</div>`
      : "";
    return `<div style="border:1px solid #e2e8f0;border-radius:6px;` +
      `padding:12px 16px;margin:0 0 12px;">` +
      `<p style="font-weight:bold;color:#2d3748;margin:0 0 6px;">` +
      `<strong>${i + 1})</strong> ${title}` +
      (variant ? ` <span style="color:#718096;font-weight:normal;">` +
        `· ${variant}</span>` : "") +
      `</p>` +
      personBlock +
      `</div>`;
  }).join("");
};

const emailWrapper = (bodyHtml, footer) =>
  `<!DOCTYPE html><html><body style="margin:0;padding:0;background:#fdf7f4;` +
  `font-family:Arial,sans-serif;">` +
  `<table width="100%" cellpadding="0" cellspacing="0">` +
  `<tr><td align="center" style="padding:32px 16px;">` +
  `<table width="560" cellpadding="0" cellspacing="0" style="background:#ffffff;` +
  `border-radius:8px;box-shadow:0 1px 4px rgba(0,0,0,0.08);overflow:hidden;">` +
  `<tr><td style="background:${BRAND};padding:20px 32px;">` +
  `<span style="color:#ffffff;font-size:18px;font-weight:bold;">${SHOP.name}</span>` +
  `</td></tr>` +
  `<tr><td style="padding:32px;">${bodyHtml}</td></tr>` +
  `<tr><td style="padding:16px 32px;border-top:1px solid #e8d8d0;` +
  `font-size:12px;color:#a0aec0;text-align:center;">` +
  `${footer || `© ${SHOP.name} · ${SHOP.email}`}` +
  `</td></tr>` +
  `</table></td></tr></table></body></html>`;

const buildEmailHtml = (
  bodyText, name, link, order, feedback, items, footer,
) => {
  const button = linkButton(link);
  const itemsHtml = itemsBlock(items);
  const feedbackBlock = feedback
    ? `<div style="background:#fdf7f4;border-left:3px solid #B8A09A;` +
      `padding:10px 14px;margin:8px 0;color:#2d3748;font-style:italic;">` +
      `${feedback}</div>`
    : "";
  const lines = bodyText
    .replace(/\{\{NAME\}\}/g, name || "Kunde")
    .replace(/\{\{ORDER\}\}/g, order || "")
    .split("\n");
  const parts = lines.map((line) => {
    if (line.trim() === "{{LINK}}")     return button;
    if (line.trim() === "{{ITEMS}}")    return itemsHtml;
    if (line.trim() === "{{FEEDBACK}}") return feedbackBlock;
    if (line.trim() === "")             return `<div style="height:10px;"></div>`;
    return (
      `<p style="color:#4a5568;line-height:1.5;margin:0 0 8px;">${line}</p>`
    );
  });
  return emailWrapper(parts.join(""), footer);
};

const formatDateDE = (iso) => {
  const months = [
    "Januar", "Februar", "März", "April", "Mai", "Juni",
    "Juli", "August", "September", "Oktober", "November", "Dezember",
  ];
  const [y, m, d] = iso.split("-").map(Number);
  return `${d}. ${months[m - 1]} ${y}`;
};

const escHtml = (s) => String(s)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const buildReceiptEmailHtml = ({
  openingText, name, order, feedback,
  items, shipByDate, addressRaw, isModification, footer,
}) => {
  const ordNum = order || "";
  const safeAddr = addressRaw
    ? escHtml(addressRaw).replace(/\n/g, "<br>") : "";
  const td1 = "width:140px;font-weight:bold;color:#4a5568;" +
    "border:1px solid #e2e8f0;padding:10px 14px;";
  const td2 = "color:#2d3748;border:1px solid #e2e8f0;padding:10px 14px;";
  const sec = "margin-top:18px;font-size:14px;color:#2d3748;";
  const lbl = "font-weight:bold;color:#4a5568;margin:0 0 6px;";

  const openingHtml = (openingText || "")
    .replace(/\{\{NAME\}\}/g, escHtml(name || "Kunde"))
    .replace(/\{\{ORDER\}\}/g, ordNum)
    .split("\n")
    .map((line) => line.trim() === ""
      ? `<div style="height:10px;"></div>`
      : `<p style="color:#2d3748;font-size:15px;margin:0 0 8px;">${line}</p>`)
    .join("");

  const decisionLabel = isModification ? "⚠️ Änderungswunsch" : "✅ Bestätigt";

  const deliverySec = shipByDate
    ? `<div style="${sec}"><p style="${lbl}">🚚 Lieferzeit</p>` +
      `<p style="margin:0;">Übergabe an DHL bis zum ` +
      `${formatDateDE(shipByDate)}</p></div>` : "";

  const addrSec = safeAddr
    ? `<div style="${sec}"><p style="${lbl}">🏠 Adresse</p>` +
      `<p style="margin:0;line-height:1.6;">${safeAddr}</p></div>` : "";

  // Uses the shared itemsBlock() helper so the receipt email uses the same
  // multi-personalization rendering as the welcome/reminder emails.
  const itemsSec = items && items.length > 0
    ? `<div style="${sec}"><p style="${lbl}">✍️ Personalisierung</p>` +
      itemsBlock(items) +
      `</div>` : "";

  const fbSec = isModification && feedback
    ? `<div style="${sec}background:#fff8e1;border-left:4px solid` +
      ` #f6ad55;padding:12px 16px;border-radius:4px;">` +
      `<p style="${lbl}margin:0 0 6px;">📝 Deine Änderungswünsche</p>` +
      `<p style="margin:0;">${escHtml(feedback)}</p></div>` : "";

  return emailWrapper(
    openingHtml +
    `<table width="100%" cellpadding="8" cellspacing="0"` +
    ` style="border-collapse:collapse;font-size:14px;margin-top:18px;">` +
    `<tr style="background:#f7fafc;">` +
    `<td style="${td1}">Bestellung</td>` +
    `<td style="${td2}">#${ordNum}</td></tr>` +
    `<tr><td style="${td1}">Antwort</td>` +
    `<td style="${td2}">${decisionLabel}</td></tr>` +
    `</table>` +
    deliverySec + addrSec + itemsSec + fbSec,
    footer,
  );
};

// ─── Shared input/textarea styles ─────────────────────────────────────────────

const labelStyle = {
  display: "block", fontWeight: "600", fontSize: "0.78rem",
  color: "#4a5568", marginBottom: "0.3em",
  textTransform: "uppercase", letterSpacing: "0.04em",
};
const inputStyle = {
  width: "100%", boxSizing: "border-box",
  padding: "0.5em 0.75em", border: "1px solid #e8d8d0",
  borderRadius: "6px", fontSize: "0.875rem",
  color: "#2d3748", background: "#fff",
  fontFamily: "inherit", marginBottom: "1.1em",
};
const taStyle = { ...inputStyle, resize: "vertical" };

// ─── Tab definitions ──────────────────────────────────────────────────────────

const TABS = [
  {
    key: "welcome",
    label: "Willkommens-E-Mail",
    subjectKey: "welcomeSubject",
    bodyKey: "welcomeBody",
  },
  {
    key: "reminder1",
    label: "1. Erinnerung",
    subjectKey: "orderReminder1Subject",
    bodyKey: "orderReminder1Body",
  },
  {
    key: "reminder2",
    label: "2. Erinnerung",
    subjectKey: "orderReminder2Subject",
    bodyKey: "orderReminder2Body",
  },
  {
    key: "reminder3",
    label: "3. Erinnerung",
    subjectKey: "orderReminder3Subject",
    bodyKey: "orderReminder3Body",
  },
  {
    key: "etsy2",
    label: "Etsy 2. Erinnerung",
    type: "etsy",
    bodyKey: "orderEtsy2Reminder",
  },
  {
    key: "etsy3",
    label: "Etsy 3. Erinnerung",
    type: "etsy",
    bodyKey: "orderEtsy3Reminder",
  },
  {
    key: "confirmed",
    label: "✅ Bestätigt",
    type: "receipt",
    subjectKey: "orderConfirmedReceiptSubject",
    openingKey: "orderConfirmedReceiptOpening",
    isModification: false,
  },
  {
    key: "modification",
    label: "⚠️ Änderung",
    type: "receipt",
    subjectKey: "orderModificationReceiptSubject",
    openingKey: "orderModificationReceiptOpening",
    isModification: true,
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

const OrderEmailPreview = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [editMode,  setEditMode]  = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [saved,     setSaved]     = useState(false);
  const [form,      setForm]      = useState(null);

  // Sample data for preview
  const [firstName,       setFirstName]       = useState("Selin");
  const [orderNumber,     setOrderNumber]     = useState("4013717778");
  const [previewFeedback, setPreviewFeedback] = useState(
    "Bitte \u00e4ndere den Namen auf \u201eKassian\u201c statt \u201eKasian\u201c.",
  );
  // Sample items — one legacy single-field, one new Multi-Personalization,
  // one File-Upload, so Alexandra sees every rendering variant in the preview.
  const SAMPLE_ITEMS = [
    {
      title: "Taufkerze mit Trockenblumen Maya",
      variant: "Blumenkranz, Modern",
      quantity: 1,
      personalization:
        "Thessa 09.05.2026\nDenn er hat seinen Engeln befohlen, dass sie" +
        " dich behüten auf allen deinen Wegen. Psalm 91,11",
    },
    {
      title: "Stammbuch \"Julia & Niklas\"",
      variant: "Format A5 | Farbe Beige",
      quantity: 1,
      personalizations: [
        { label: "Namen des Paares", value: "Maren & Felix", type: "text" },
        { label: "Familienname",     value: "Kluck",         type: "text" },
        { label: "Hochzeitsdatum",   value: "15.10.2026",    type: "text" },
        { label: "Foto",
          value: "https://example.com/foto.jpg", type: "file" },
      ],
    },
  ];

  useEffect(() => {
    const load = async () => {
      const snap = await getDoc(doc(db, "config", "orderEmailTexts"));
      setForm(snap.exists()
        ? { ...ORDER_EMAIL_TEXT_DEFAULTS, ...snap.data() }
        : { ...ORDER_EMAIL_TEXT_DEFAULTS });
    };
    load();
  }, []);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, "config", "orderEmailTexts"), form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      setEditMode(false);
    } catch (err) {
      alert("Speichern fehlgeschlagen: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return (
      <p style={{ color: "#718096", fontStyle: "italic", padding: "2em" }}>
        Lade E-Mail-Konfiguration…
      </p>
    );
  }

  const tabStyle = (active) => ({
    padding: "0.45em 1.1em", borderRadius: "6px",
    border: `1px solid ${active ? BRAND : "#e8d8d0"}`,
    background: active ? BRAND : "#fff",
    color: active ? "#fff" : "#4a5568",
    fontWeight: "600", fontSize: "0.85rem", cursor: "pointer",
  });

  // ── Edit mode ─────────────────────────────────────────────────────────────────
  if (editMode) {
    const t = TABS[activeTab];
    const isModTab = t.key === "modification";
    const isWelcomeTab = t.key === "welcome";
    const placeholderHint = isModTab
      ? "Platzhalter: {{NAME}}, {{LINK}}, {{ORDER}}, {{FEEDBACK}}"
      : isWelcomeTab
        ? "Platzhalter: {{NAME}}, {{LINK}}, {{ORDER}}, {{ITEMS}}"
        : "Platzhalter: {{NAME}}, {{LINK}}, {{ORDER}}";

    return (
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        {/* Banner */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: "#ebf8ff", border: "1px solid #90cdf4",
          borderRadius: "8px", padding: "0.75em 1.25em",
          marginBottom: "1.5em", gap: "1em",
        }}>
          <span style={{ color: "#2c5282", fontSize: "0.875rem", fontWeight: "500" }}>
            ✏️ Bearbeitungsmodus — Änderungen wirken sich auf die Live-Emails aus.
          </span>
          <button onClick={() => setEditMode(false)} style={{
            padding: "0.35em 0.9em", borderRadius: "6px",
            border: "1px solid #90cdf4", background: "#fff",
            color: "#2c5282", fontSize: "0.8rem", fontWeight: "600",
            cursor: "pointer",
          }}>
            ← Vorschau
          </button>
        </div>

        <div style={{
          background: "#fff", borderRadius: "10px",
          boxShadow: "0 1px 4px rgba(74,44,42,0.08)", padding: "2em",
        }}>
          <h3 style={{ margin: "0 0 1.5em", fontSize: "1rem", color: "#1a202c" }}>
            Bestell-E-Mail-Texte bearbeiten
          </h3>

          {/* Global settings */}
          <div style={{
            background: "#fdf7f4", border: "1px solid #e8d8d0",
            borderRadius: "8px", padding: "1.1em 1.25em", marginBottom: "1.75em",
          }}>
            <p style={{
              margin: "0 0 0.75em", fontWeight: "700", fontSize: "0.85rem",
              color: "#4a2c2a", textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}>
              Globale Einstellungen
            </p>

            {/* Sender address */}
            <label style={{ ...labelStyle, marginBottom: "0.25em" }}>
              Absender-Adresse
            </label>
            <div style={{ display: "flex", alignItems: "center",
              gap: "0.4em", marginBottom: "1em" }}>
              <input
                style={{ ...inputStyle, width: "160px", marginBottom: 0 }}
                value={form.fromLocal || ""}
                onChange={(e) => setField("fromLocal", e.target.value)}
                placeholder="gestaltung"
              />
              <span style={{ color: "#4a5568", fontSize: "0.875rem",
                whiteSpace: "nowrap" }}>
                @{SHOP.email.split("@")[1]}
              </span>
            </div>

            {/* Footer text */}
            <label style={{ ...labelStyle, marginBottom: "0.25em" }}>
              E-Mail-Footer
            </label>
            <input
              style={{ ...inputStyle, marginBottom: "1em" }}
              value={form.emailFooter || ""}
              onChange={(e) => setField("emailFooter", e.target.value)}
            />

            {/* Reminder days */}
            <p style={{
              margin: "0 0 0.5em", fontWeight: "600", fontSize: "0.8rem",
              color: "#4a5568", textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}>
              Erinnerungs-Zeitplan (Tage nach Welcome-Mail)
            </p>
            <div style={{ display: "flex", gap: "1.5em", flexWrap: "wrap" }}>
              {[1, 2, 3].map((n) => (
                <label key={n} style={{ display: "flex", alignItems: "center",
                  gap: "0.5em", fontSize: "0.875rem", color: "#4a5568" }}>
                  <span style={{ fontWeight: "600" }}>{n}. Erinnerung nach</span>
                  <input
                    type="number" min="1"
                    value={form[`orderReminder${n}Days`]}
                    onChange={(e) =>
                      setField(`orderReminder${n}Days`, Number(e.target.value))}
                    style={{
                      width: "60px", padding: "0.3em 0.5em",
                      border: "1px solid #e8d8d0", borderRadius: "5px",
                      fontSize: "0.875rem", textAlign: "center",
                    }}
                  />
                  <span>Tagen</span>
                </label>
              ))}
            </div>

            {/* Event date urgency settings */}
            <p style={{
              margin: "1em 0 0.5em", fontWeight: "600", fontSize: "0.8rem",
              color: "#4a5568", textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}>
              Personalisierungs-Datum
            </p>
            <label style={{ ...labelStyle, marginBottom: "0.25em" }}>
              Mindest-Werktage vor Ereignisdatum
            </label>
            <input
              type="number" min="0"
              value={form.minDaysBeforeEvent ?? 5}
              onChange={(e) =>
                setField("minDaysBeforeEvent", Number(e.target.value))}
              style={{
                width: "80px", padding: "0.3em 0.5em",
                border: "1px solid #e8d8d0", borderRadius: "5px",
                fontSize: "0.875rem", textAlign: "center",
                marginBottom: "1em", display: "block",
              }}
            />
            <label style={{ ...labelStyle, marginBottom: "0.25em" }}>
              Warntext im Kundenformular (bei Datumsüberschreitung)
            </label>
            <input
              style={{ ...inputStyle, marginBottom: 0 }}
              value={form.eventDateWarningText || ""}
              onChange={(e) =>
                setField("eventDateWarningText", e.target.value)}
              placeholder="Bitte prüfen: das Datum der Personalisierung…"
            />
          </div>

          {/* Tab selector */}
          <div style={{ display: "flex", gap: "0.5em",
            flexWrap: "wrap", marginBottom: "1.5em" }}>
            {TABS.map((tab, i) => (
              <button key={tab.key} style={tabStyle(activeTab === i)}
                onClick={() => setActiveTab(i)}>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Subject / Body — branched by tab type */}
          {t.type === "etsy" ? (
            <>
              <div style={{
                background: "#fffbeb", border: "1px solid #f6e05e",
                borderRadius: "6px", padding: "0.6em 0.9em", marginBottom: "1em",
                fontSize: "0.82rem", color: "#744210",
              }}>
                ℹ️ Diese Nachricht wird als Etsy Conversation an den Käufer gesendet — kein HTML, kein Betreff, nur Klartext.
              </div>
              <label style={labelStyle}>
                {"Text — Platzhalter: {{NAME}}, {{ORDER}}"}</label>
              <textarea
                style={{ ...taStyle, minHeight: "200px" }}
                value={form[t.bodyKey] || ""}
                onChange={(e) => setField(t.bodyKey, e.target.value)}
              />
            </>
          ) : t.type === "receipt" ? (
            <>
              <label style={labelStyle}>
                {"Betreff — Platzhalter: {{ORDER}}"}
              </label>
              <input
                style={inputStyle}
                value={form[t.subjectKey] || ""}
                onChange={(e) => setField(t.subjectKey, e.target.value)}
              />
              <div style={{
                background: "#fffbeb", border: "1px solid #f6e05e",
                borderRadius: "6px", padding: "0.6em 0.9em",
                marginBottom: "0.75em", fontSize: "0.82rem", color: "#744210",
              }}>
                ℹ️ Dieser Text erscheint als Einleitung. Darunter folgen Bestellnummer, Lieferdatum, Adresse und Personalisierung automatisch.
              </div>
              <label style={labelStyle}>
                {"Eröffnungstext — Platzhalter: {{NAME}}, {{ORDER}}"}
              </label>
              <textarea
                style={{ ...taStyle, minHeight: "160px" }}
                value={form[t.openingKey] || ""}
                onChange={(e) => setField(t.openingKey, e.target.value)}
              />
            </>
          ) : (
            <>
              <label style={labelStyle}>Betreff</label>
              <input
                style={inputStyle}
                value={form[t.subjectKey] || ""}
                onChange={(e) => setField(t.subjectKey, e.target.value)}
              />
              <label style={labelStyle}>Text — {placeholderHint}</label>
              <textarea
                style={{ ...taStyle, minHeight: "260px" }}
                value={form[t.bodyKey] || ""}
                onChange={(e) => setField(t.bodyKey, e.target.value)}
              />
            </>
          )}

          <button onClick={handleSave} disabled={saving} style={{
            padding: "0.65em 2em", borderRadius: "7px",
            border: "none", background: saving ? "#a0aec0" : BRAND,
            color: "#fff", fontWeight: "700", fontSize: "0.95rem",
            cursor: saving ? "not-allowed" : "pointer",
          }}>
            {saving ? "Wird gespeichert…" : "Änderungen speichern"}
          </button>
        </div>
      </div>
    );
  }

  // ── Preview mode ──────────────────────────────────────────────────────────────
  const SAMPLE_SHIP_DATE = "2026-06-20";
  const SAMPLE_ADDRESS = "Maria Mustermann\nMusterstraße 12\n12345 Berlin";

  const currentTab = TABS[activeTab];
  const isReceiptTab = currentTab.type === "receipt";
  const isModTab = currentTab.key === "modification";
  const applyPlaceholders = (text) => text
    .replace(/\{\{NAME\}\}/g, firstName || "Kunde")
    .replace(/\{\{ORDER\}\}/g, orderNumber || "");
  const previewHtml = isReceiptTab
    ? buildReceiptEmailHtml({
        openingText: form[currentTab.openingKey] || "",
        name: firstName,
        order: orderNumber,
        feedback: previewFeedback,
        items: SAMPLE_ITEMS,
        shipByDate: SAMPLE_SHIP_DATE,
        addressRaw: SAMPLE_ADDRESS,
        isModification: currentTab.isModification,
        footer: form.emailFooter,
      })
    : buildEmailHtml(
        form[currentTab.bodyKey] || "",
        firstName,
        SAMPLE_LINK,
        orderNumber,
        isModTab ? previewFeedback : "",
        SAMPLE_ITEMS,
        form.emailFooter,
      );

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      {/* Header row */}
      <div style={{
        display: "flex", alignItems: "center",
        justifyContent: "space-between", marginBottom: "1em", gap: "1em",
      }}>
        <h2 style={{ margin: 0, fontSize: "1.25rem", color: "#1a202c" }}>
          Bestell-E-Mails Vorschau
        </h2>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75em" }}>
          {saved && (
            <span style={{ color: "#276749", fontWeight: "700",
              fontSize: "0.85rem" }}>
              ✓ Gespeichert!
            </span>
          )}
          <button onClick={() => setEditMode(true)} style={{
            padding: "0.45em 1.1em", borderRadius: "6px",
            border: "1px solid #e8d8d0", background: "#fff",
            color: "#4a5568", fontWeight: "600", fontSize: "0.85rem",
            cursor: "pointer",
          }}>
            ✏️ Text bearbeiten
          </button>
        </div>
      </div>

      {/* Settings + sample data bar */}
      <div style={{
        background: "#fff", borderRadius: "10px",
        boxShadow: "0 1px 4px rgba(74,44,42,0.08)",
        padding: "1em 1.5em", marginBottom: "1.25em",
        display: "flex", gap: "2em", flexWrap: "wrap", alignItems: "flex-end",
      }}>
        {/* Reminder schedule (read-only) */}
        <div>
          <div style={{
            fontWeight: "700", fontSize: "0.78rem", color: "#4a2c2a",
            textTransform: "uppercase", letterSpacing: "0.04em",
            marginBottom: "0.4em",
          }}>
            Erinnerungs-Zeitplan
          </div>
          <div style={{ display: "flex", gap: "1.25em",
            fontSize: "0.85rem", color: "#4a5568" }}>
            {[1, 2, 3].map((n) => (
              <span key={n}>
                <strong>{n}.</strong> nach {form[`orderReminder${n}Days`]} Tagen
              </span>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div style={{
          width: "1px", background: "#e8d8d0", alignSelf: "stretch",
        }} />

        {/* Sample data inputs */}
        <div style={{ display: "flex", gap: "1.25em",
          alignItems: "flex-end", flexWrap: "wrap" }}>
          <div>
            <label style={{ ...labelStyle, marginBottom: "0.25em" }}>
              Vorschau-Name
            </label>
            <input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              style={{ ...inputStyle, width: "150px", marginBottom: 0 }}
              placeholder="z.B. Selin"
            />
          </div>
          <div>
            <label style={{ ...labelStyle, marginBottom: "0.25em" }}>
              Bestellnummer
            </label>
            <input
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              style={{ ...inputStyle, width: "150px", marginBottom: 0 }}
              placeholder="z.B. 4013717778"
            />
          </div>
          {isModTab && (
            <div>
              <label style={{ ...labelStyle, marginBottom: "0.25em" }}>
                Änderungstext (Vorschau)
              </label>
              <input
                value={previewFeedback}
                onChange={(e) => setPreviewFeedback(e.target.value)}
                style={{ ...inputStyle, width: "280px", marginBottom: 0 }}
                placeholder="z.B. Bitte Namen ändern…"
              />
            </div>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: "flex", gap: "0.5em",
        flexWrap: "wrap", marginBottom: "1em" }}>
        {TABS.map((tab, i) => (
          <button key={tab.key} style={tabStyle(activeTab === i)}
            onClick={() => setActiveTab(i)}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Feedback input — shown for modification receipt tab too */}
      {isReceiptTab && currentTab.isModification && (
        <div style={{
          background: "#fff", borderRadius: "10px",
          boxShadow: "0 1px 4px rgba(74,44,42,0.08)",
          padding: "0.75em 1.5em", marginBottom: "1em",
          display: "flex", gap: "1.25em", alignItems: "flex-end",
        }}>
          <div>
            <label style={{ ...labelStyle, marginBottom: "0.25em" }}>
              Änderungstext (Vorschau)
            </label>
            <input
              value={previewFeedback}
              onChange={(e) => setPreviewFeedback(e.target.value)}
              style={{ ...inputStyle, width: "360px", marginBottom: 0 }}
              placeholder="z.B. Bitte Namen ändern…"
            />
          </div>
        </div>
      )}

      {/* Subject line + email iframe — or Etsy message preview */}
      {currentTab.type === "etsy" ? (
        <div style={{
          border: "1px solid #e8d8d0", borderRadius: "8px", overflow: "hidden",
          boxShadow: "0 1px 4px rgba(74,44,42,0.08)",
        }}>
          <div style={{
            background: "#f7f7f7", borderBottom: "1px solid #e2e8f0",
            padding: "0.75em 1.25em", fontSize: "0.82rem",
            color: "#718096", fontWeight: "600",
          }}>
            Etsy Nachricht (Vorschau) — Konversation an Käufer
          </div>
          <div style={{ background: "#fff", padding: "1.5em" }}>
            <div style={{
              background: "#f7f7f7", borderRadius: "12px",
              padding: "1em 1.25em", maxWidth: "480px",
              fontSize: "0.875rem", color: "#2d3748",
              whiteSpace: "pre-line", lineHeight: "1.6",
            }}>
              {applyPlaceholders(form[currentTab.bodyKey] || "")}
            </div>
            <div style={{ fontSize: "0.75rem", color: "#a0aec0", marginTop: "0.5em" }}>
              {SHOP.name} · gerade eben
            </div>
          </div>
        </div>
      ) : (
        <>
          <div style={{
            background: "#fff", borderRadius: "8px 8px 0 0",
            border: "1px solid #e8d8d0", borderBottom: "none",
            padding: "0.75em 1.25em", fontSize: "0.875rem", color: "#4a5568",
          }}>
            <span style={{ fontWeight: "600", color: "#2d3748" }}>Betreff: </span>
            {applyPlaceholders(form[currentTab.subjectKey] || "")}
          </div>
          <iframe
            key={`${activeTab}-${firstName}-${JSON.stringify(
              isReceiptTab
                ? form[currentTab.openingKey]
                : form[currentTab.bodyKey]
            )}`}
            srcDoc={previewHtml}
            sandbox="allow-same-origin"
            style={{
              width: "100%", height: "680px",
              border: "1px solid #e8d8d0", borderRadius: "0 0 8px 8px",
              boxShadow: "0 1px 4px rgba(74,44,42,0.08)", display: "block",
            }}
            title={`Bestell-E-Mail Vorschau: ${currentTab.label}`}
          />
        </>
      )}
    </div>
  );
};

export default OrderEmailPreview;
